import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import { sharePermissionLabel } from '@/components/review/sharePermission';
import type { GroupSharedSession } from '@/types';

function formatDueDate(iso?: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('zh-HK', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface Props {
  session: GroupSharedSession;
  groupId: string;
  canManage?: boolean;
  onToggleOfficialRelease?: (shareId: string, sessionId: string, published: boolean) => Promise<void>;
  onEditShare?: (session: GroupSharedSession) => void;
}

export default function GroupSessionCard({
  session,
  groupId,
  canManage,
  onToggleOfficialRelease,
  onEditShare,
}: Props) {
  const navigate = useNavigate();
  const [toggling, setToggling] = useState(false);
  const progress = session.participant_progress;
  const progressPct =
    progress && progress.total > 0 ? Math.round((progress.responded / progress.total) * 100) : 0;
  const isParticipantMode = session.permission === 'participant';
  const showReleasedChip = !canManage && isParticipantMode && !!session.official_released_at;

  const openReview = () => navigate(`/review/${session.session_id}?group_id=${groupId}`);

  return (
    <Card
      variant="outlined"
      onClick={openReview}
      sx={{
        borderRadius: 2.5,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.15s',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'secondary.main',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.secondary.main, t.palette.mode === 'dark' ? 0.18 : 0.22),
              color: 'secondary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <VideoLibraryOutlinedIcon />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={800} noWrap>
                  {session.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[session.home_team, session.away_team].filter(Boolean).join(' vs ') || '—'}
                  {session.match_date ? ` · ${session.match_date}` : ''}
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0, mt: 0.25 }} />
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              <Chip
                size="small"
                variant="outlined"
                color={isParticipantMode ? 'secondary' : 'default'}
                label={sharePermissionLabel(session.permission as 'participant' | 'viewer')}
                sx={{ height: 24 }}
              />
              {canManage && isParticipantMode && (
                <Chip
                  size="small"
                  color={session.official_released_at ? 'success' : 'default'}
                  variant={session.official_released_at ? 'filled' : 'outlined'}
                  label={session.official_released_at ? '已發佈官方判決' : '未發佈'}
                  sx={{ height: 24 }}
                />
              )}
              {showReleasedChip && (
                <Chip size="small" color="success" label="已發佈官方判決" sx={{ height: 24 }} />
              )}
              {isParticipantMode && session.due_at && (
                <Chip
                  size="small"
                  color={session.is_overdue ? 'error' : 'warning'}
                  variant="outlined"
                  label={`截止 ${formatDueDate(session.due_at)}`}
                  sx={{ height: 24 }}
                />
              )}
            </Stack>

            {session.message && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {session.message}
              </Typography>
            )}

            {canManage && isParticipantMode && progress && progress.total > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    參與者回覆
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress.responded}/{progress.total} · {progressPct}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  color={progressPct >= 100 ? 'success' : 'secondary'}
                  sx={{ height: 5, borderRadius: 3 }}
                />
              </Box>
            )}

            {session.official_source_user_name && canManage && isParticipantMode && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                官方判決來源：{session.official_source_user_name}
              </Typography>
            )}

            <Typography variant="caption" color="secondary.main" sx={{ mt: 1.25, display: 'block', fontWeight: 600 }}>
              點擊卡片開啟影片分析
            </Typography>
          </Box>
        </Stack>

        {canManage && (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="flex-end"
            sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
            onClick={(e) => e.stopPropagation()}
          >
            {session.can_edit_share && onEditShare && (
              <Tooltip title="分享設定">
                <IconButton size="small" onClick={() => onEditShare(session)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isParticipantMode && (
              <>
                <Tooltip title="查看群組回覆">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/review/${session.session_id}?group_id=${groupId}&tab=analytics`)}
                  >
                    <AnalyticsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {onToggleOfficialRelease && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      發佈
                    </Typography>
                    <Switch
                      size="small"
                      color="secondary"
                      checked={!!session.official_released_at}
                      disabled={toggling}
                      onChange={async (_, checked) => {
                        setToggling(true);
                        try {
                          await onToggleOfficialRelease(session.share_id, session.session_id, checked);
                        } finally {
                          setToggling(false);
                        }
                      }}
                    />
                  </Stack>
                )}
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
